import axios from '@/api/axios-config'
import type { SnowflakeId } from '@/types/snowflake'
import type {
  Announcement, AnnouncementMineItem, AnnouncementDraft, SnoozeKind,
  AnnouncementStats, AudiencePreview, AckList,
} from '@/features/announcements/types'

export interface AnnouncementSearchResult {
  /** Backend BaseListResponse shape: rows under `results`, count under `pagination.totalResults`. */
  results: Announcement[]
  pagination?: { page: number; pageSize: number; totalResults: number; totalPages: number }
}

/** Endpoints under /system/announcements (axios interceptor unwraps {data} → returns raw payload). */
export const announcementApi = {
  // ---- 用户接收侧 ----
  pending: () => axios.get('/system/announcements/pending') as Promise<Announcement[]>,
  mine: () => axios.get('/system/announcements/mine') as Promise<AnnouncementMineItem[]>,
  unreadCount: () =>
    axios.get('/system/announcements/mine/unread-count') as Promise<{ count: number }>,
  detail: (id: SnowflakeId) =>
    axios.get(`/system/announcements/${id}`) as Promise<Announcement>,
  markRead: (id: SnowflakeId) => axios.post(`/system/announcements/${id}/read`, {}),
  snooze: (id: SnowflakeId, kind: SnoozeKind) =>
    axios.post(`/system/announcements/${id}/snooze`, { kind }) as Promise<{ snoozedUntil: string }>,
  acknowledge: (id: SnowflakeId) =>
    axios.post(`/system/announcements/${id}/acknowledge`, {}),

  // ---- Admin 管理侧 ----
  search: (data: Record<string, unknown>) =>
    axios.post('/system/announcements/search', data) as Promise<AnnouncementSearchResult>,
  adminDetail: (id: SnowflakeId) =>
    axios.get(`/system/announcements/${id}/edit`) as Promise<Announcement>,
  create: (data: AnnouncementDraft) =>
    axios.post('/system/announcements', data) as Promise<Announcement>,
  update: (id: SnowflakeId, data: Partial<AnnouncementDraft>) =>
    axios.put(`/system/announcements/${id}`, data) as Promise<Announcement>,
  publish: (id: SnowflakeId) =>
    axios.post(`/system/announcements/${id}/publish`, {}) as Promise<Announcement>,
  cancelSchedule: (id: SnowflakeId) =>
    axios.post(`/system/announcements/${id}/cancel-schedule`, {}) as Promise<Announcement>,
  archive: (id: SnowflakeId) =>
    axios.post(`/system/announcements/${id}/archive`, {}) as Promise<Announcement>,
  remove: (id: SnowflakeId) => axios.delete(`/system/announcements/${id}`),
  stats: (id: SnowflakeId) =>
    axios.get(`/system/announcements/${id}/stats`) as Promise<AnnouncementStats>,
  acknowledgements: (id: SnowflakeId) =>
    axios.get(`/system/announcements/${id}/acknowledgements`) as Promise<AckList>,
  /** CSV blob (Bearer auth via axios). Caller triggers the download. */
  acknowledgementsExport: (id: SnowflakeId) =>
    axios.get(`/system/announcements/${id}/acknowledgements/export`, { responseType: 'blob' }) as unknown as Promise<Blob>,
  audiencePreview: (audienceFilter: Record<string, unknown>) =>
    axios.post('/system/announcements/audience-preview', { audienceFilter }) as Promise<AudiencePreview>,
}
